import { numberToWords } from "@/lib/invoiceTemplate";

const inr = (v) => `₹ ${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
const fmtD = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return s; }
};

const TERMS = [
  "The maximum loan amount under this facility is ₹2,20,000 (Rupees Two Lakh Twenty Thousand only).",
  "The maximum repayment tenure is 72 (seventy-two) months.",
  "Interest is charged at the rate of 24% per annum on a reducing-balance basis.",
  "This formal loan agreement is executed in the employee’s name prior to disbursement of the loan.",
  "As a condition of this loan, the Employee signs a separate Service Commitment Agreement undertaking to remain in the continuous service of VNS Vinayaka Group of Enterprises for a period equal to the loan tenure, counted from the date of disbursement.",
  "The agreed EMI shall be deducted directly from the employee’s salary on the 11th of each month, subject to applicable laws and the employee’s authorization.",
  "The EMI amount and complete repayment schedule form part of this agreement (see the Repayment Schedule below).",
  "If the employee resigns, abandons employment, or otherwise ceases to work with the enterprise before the loan is fully repaid, the entire outstanding amount shall remain payable. The enterprise may initiate appropriate recovery procedures and, where necessary, pursue legal remedies in accordance with this signed agreement and applicable law.",
  "The employee confirms that they have carefully reviewed and understood all terms and conditions before accepting this loan.",
];

function scheduleRows(schedule) {
  return schedule.map((s) =>
    `<tr>
      <td class="c">${s.month}</td>
      <td>${s.due_date ? fmtD(s.due_date) : "—"}</td>
      <td class="num">${inr(s.emi)}</td>
      <td class="num">${inr(s.principal)}</td>
      <td class="num">${inr(s.interest)}</td>
      <td class="num">${inr(s.balance)}</td>
    </tr>`).join("");
}

function docHTML(loan, origin) {
  return `
  <div class="page">
    <img class="watermark" src="${origin}/vns-logo.png" alt="" />
    <div class="confid">CONFIDENTIAL &mdash; ${esc(loan.employee_name || loan.name || "")}</div>
    <header>
      <img src="${origin}/vns-logo.png" alt="VNS" />
      <div class="co">
        <h1>VNS Vinayaka Group of Enterprises</h1>
        <div class="sub">Data Entry Division</div>
      </div>
    </header>
    <div class="title">Employee Loan Agreement</div>
    <div class="meta">
      <span><b>Agreement No:</b> ${esc(loan.agreement_number || "—")}</span>
      <span><b>Date:</b> ${fmtD(loan.disburse_date || new Date().toISOString())}</span>
    </div>

    <p class="para">This Loan Agreement ("Agreement") is made between <b>VNS Vinayaka Group of Enterprises</b>
    ("the Enterprise", the Lender) and the employee named below ("the Employee", the Borrower).</p>

    <table class="grid">
      <tr>
        <td style="width:50%"><span class="lbl">Employee Name</span><span class="val"><b>${esc(loan.employee_name)}</b></span></td>
        <td style="width:25%"><span class="lbl">Employee ID</span><span class="val">${esc(loan.employee_id)}</span></td>
        <td style="width:25%"><span class="lbl">Designation</span><span class="val">${esc(loan.designation || "—")}</span></td>
      </tr>
    </table>

    <table class="fin">
      <tr><td class="k">Loan (Principal) Amount</td><td class="num">${inr(loan.principal)}</td>
          <td class="k">Interest Rate</td><td class="num">${loan.annual_rate}% p.a. (reducing balance)</td></tr>
      <tr><td class="k">Repayment Tenure</td><td class="num">${loan.tenure_months} months</td>
          <td class="k">Monthly EMI</td><td class="num"><b>${inr(loan.emi)}</b></td></tr>
      <tr><td class="k">Total Interest Payable</td><td class="num">${inr(loan.total_interest)}</td>
          <td class="k">Total Amount Payable</td><td class="num"><b>${inr(loan.total_payable)}</b></td></tr>
    </table>
    <div class="words"><b>Loan amount in words:</b> Indian Rupees ${esc(numberToWords(loan.principal))} Only.</div>

    <div class="section">Terms &amp; Conditions</div>
    <ol class="terms">${TERMS.map((t) => `<li>${esc(t)}</li>`).join("")}</ol>

    <div class="section">Repayment Schedule</div>
    <table class="sched">
      <thead><tr><th class="c">#</th><th>Due Date</th><th class="num">EMI</th><th class="num">Principal</th><th class="num">Interest</th><th class="num">Balance</th></tr></thead>
      <tbody>${scheduleRows(loan.schedule || [])}</tbody>
    </table>

    <div class="declare">
      <p>I, <b>${esc(loan.employee_name)}</b>, have read and understood the above terms and conditions and voluntarily
      accept this loan. I authorize VNS Vinayaka Group of Enterprises to deduct the monthly EMI of
      <b>${inr(loan.emi)}</b> from my salary until the loan is fully repaid.</p>
    </div>

    <div class="signs">
      <div class="sigcol">
        <div class="sigline"></div>
        <div class="sigcap">Employee Signature<br/>${esc(loan.employee_name)}</div>
      </div>
      <div class="sigcol">
        <img class="stamp" src="${origin}/vns-stamp.png" alt="" onerror="this.style.display='none'"/>
        <img class="sig" src="${origin}/vns-signature.png" alt="" onerror="this.style.display='none'"/>
        <div class="sigline"></div>
        <div class="sigcap">For VNS Vinayaka Group of Enterprises<br/>Authorised Signatory</div>
      </div>
    </div>
    <div class="bar">Confidential · VNS Vinayaka Group of Enterprises · Employee Loan Agreement</div>
  </div>`;
}

const STYLE = `
  :root{--navy:#0B2545;--gold:#C5A059;--ink:#0f172a;--line:#c7cdd6;--soft:#f4f6f9;}
  *{box-sizing:border-box;}
  body{margin:0;background:#e9edf2;font-family:Georgia,'Times New Roman',serif;color:var(--ink);}
  .toolbar{position:sticky;top:0;background:#0B2545;color:#fff;padding:10px 16px;font-family:Arial,sans-serif;display:flex;gap:10px;align-items:center;z-index:5;}
  .toolbar button{background:#C5A059;color:#0B2545;border:0;padding:8px 16px;border-radius:6px;font-weight:700;cursor:pointer;}
  .page{width:210mm;min-height:297mm;background:#fff;margin:16px auto;padding:16mm 14mm;box-shadow:0 6px 24px rgba(0,0,0,.15);position:relative;}
  .watermark{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:120mm;max-width:70%;opacity:.05;pointer-events:none;}
  .confid{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%) rotate(-30deg);font-size:30px;font-weight:800;letter-spacing:3px;color:#0B2545;opacity:.06;white-space:nowrap;pointer-events:none;z-index:0;}
  header{display:flex;align-items:center;gap:16px;border-bottom:3px solid var(--gold);padding-bottom:10px;}
  header img{height:62px;width:62px;object-fit:contain;}
  .co h1{margin:0;font-size:19px;color:var(--navy);}
  .co .sub{font-family:Arial,sans-serif;font-size:10px;letter-spacing:3px;color:#64748b;text-transform:uppercase;}
  .title{text-align:center;font-family:Arial,sans-serif;font-weight:700;font-size:16px;letter-spacing:3px;color:var(--navy);margin:14px 0 6px;text-transform:uppercase;}
  .meta{display:flex;justify-content:space-between;font-family:Arial,sans-serif;font-size:11px;color:#475569;margin-bottom:12px;}
  .para{font-size:12.5px;line-height:1.6;margin:8px 0;}
  .grid{width:100%;border-collapse:collapse;margin:8px 0;}
  .grid td{border:1px solid var(--line);vertical-align:top;padding:6px 8px;font-family:Arial,sans-serif;}
  .lbl{font-size:8.5px;letter-spacing:.5px;text-transform:uppercase;color:#64748b;display:block;margin-bottom:2px;}
  .val{font-size:12px;}
  .fin{width:100%;border-collapse:collapse;margin-top:6px;}
  .fin td{border:1px solid var(--line);padding:8px 10px;font-family:Arial,sans-serif;font-size:12px;}
  .fin .k{background:var(--soft);color:var(--navy);font-weight:600;width:22%;}
  .fin .num{width:28%;text-align:left;}
  .words{font-family:Arial,sans-serif;font-size:12px;margin-top:8px;padding:8px 10px;background:var(--soft);border:1px solid var(--line);}
  .bond{font-size:12px;line-height:1.6;border:1.5px solid var(--gold);background:#fbf7ee;border-radius:5px;padding:12px 14px;margin-top:4px;}
  .bond p{margin:0 0 8px;}
  .bond .grid{margin:8px 0;}
  .section{background:var(--navy);color:#fff;font-family:Arial,sans-serif;font-size:12px;letter-spacing:1px;padding:7px 10px;margin:16px 0 8px;text-transform:uppercase;border-radius:3px;}
  .terms{font-size:12px;line-height:1.6;padding-left:20px;margin:0;}
  .terms li{margin-bottom:6px;}
  .sched{width:100%;border-collapse:collapse;font-family:Arial,sans-serif;}
  .sched th{background:var(--soft);color:var(--navy);font-size:10.5px;padding:6px;border:1px solid var(--line);text-transform:uppercase;text-align:left;}
  .sched th.num, .sched td.num{text-align:right;}
  .sched th.c, .sched td.c{text-align:center;}
  .sched td{font-size:11px;padding:5px 6px;border:1px solid var(--line);}
  .sched tbody tr:nth-child(even){background:#fafbfc;}
  .declare{font-size:12px;line-height:1.6;margin-top:16px;padding:10px 12px;border:1px dashed var(--line);border-radius:4px;}
  .signs{display:flex;justify-content:space-between;margin-top:40px;gap:40px;}
  .sigcol{flex:1;text-align:center;position:relative;}
  .sigcol .stamp{height:60px;position:absolute;top:-64px;left:50%;transform:translateX(-50%);opacity:.9;}
  .sigcol .sig{height:34px;display:block;margin:0 auto 2px;}
  .sigline{border-top:1px solid #334155;margin-top:30px;}
  .sigcap{font-family:Arial,sans-serif;font-size:10.5px;color:#334155;padding-top:5px;}
  .bar{background:var(--navy);color:var(--gold);text-align:center;font-family:Arial,sans-serif;font-size:9.5px;letter-spacing:1px;padding:6px;margin-top:20px;border-radius:3px;}
  @media print{body{background:#fff;} .toolbar{display:none;} .page{box-shadow:none;margin:0;}}
`;

export function openLoanAgreement(loan) {
  const origin = window.location.origin;
  const title = `Loan Agreement · ${loan.employee_name || ""}`;
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>${title}</title><style>${STYLE}</style></head>
  <body>
    <div class="toolbar"><b>${esc(title)}</b><span style="flex:1"></span><button onclick="window.print()">Print / Save as PDF</button></div>
    ${docHTML(loan, origin)}
  </body></html>`;
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
}
